import { css, type SerializedStyles } from '@emotion/react';
import type { Theme } from '../themes/types';
import { borderWidth, transition, focusRing, shadow } from '../constants/style';
import { zIndex } from '../constants/zIndex';

/* ─── Calendar constants ─── */

/** 280px — no matching layout token; calendar-specific minimum. */
const CALENDAR_MIN_WIDTH = '17.5rem';
/** 13px — off the typography grid (nearest: 12px XS, 14px S); keep until design aligns. */
const CELL_FONT_SIZE = '0.8125rem';
const DISABLED_OPACITY = 0.4;
const OUT_OF_RANGE_OPACITY = 0.5;

/** Resolved tokens consumed by every style helper in this module. */
export interface DatepickerStyleTokens {
  getColor: Theme['getColor'];
  getRadius: Theme['getRadius'];
  getFont: Theme['getFont'];
  getSpacing: Theme['getSpacing'];
  hasError: boolean;
  hasValue: boolean;
}

/* ─── Trigger ─── */

export function triggerStyle(tokens: DatepickerStyleTokens): SerializedStyles {
  const font = tokens.getFont('M_Regular');
  const textColor = tokens.getColor('fg');
  const placeholderColor = tokens.getColor('input.text.placeholder');
  const borderColor = tokens.getColor(tokens.hasError ? 'border.error' : 'border');
  const borderBase = tokens.getColor('border');
  const accentColor = tokens.getColor('border.accent');

  return css`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 ${tokens.getSpacing('3')};
    margin: 0;
    border: none;
    border-radius: ${tokens.getRadius('sm')};
    background-color: transparent;
    cursor: pointer;
    gap: ${tokens.getSpacing('2')};
    font-family: ${font.fontFamily};
    font-size: ${font.size}px;
    font-weight: ${font.weight};
    line-height: ${font.lineHeight}px;
    letter-spacing: ${font.letterSpacing};
    color: ${tokens.hasValue ? textColor : placeholderColor};
    box-shadow: inset 0 0 0 ${borderWidth.sm} ${borderColor};
    transition: box-shadow ${transition.fast};

    &:hover {
      box-shadow: inset 0 0 0 ${borderWidth.sm} ${borderBase};
    }

    &:focus-visible {
      outline: ${focusRing.width} solid ${accentColor};
      outline-offset: ${focusRing.offset};
    }

    &:not(:focus-visible) {
      outline: none;
    }
  `;
}

export function triggerIconStyle(spacing: Theme['getSpacing']): SerializedStyles {
  return css`
    margin: ${spacing('3')} 0;
    display: inline-flex;
    align-items: center;
  `;
}

export const triggerTextStyle = css`
  flex: 1;
  text-align: left;
  display: flex;
  align-items: center;
  line-height: 1;
`;

/* ─── Content (popover panel) ─── */

export function contentStyle(tokens: DatepickerStyleTokens): SerializedStyles {
  const textColor = tokens.getColor('fg');
  const fgMuted = tokens.getColor('fg.muted');
  const accentColor = tokens.getColor('border.accent');
  const bgHover = tokens.getColor('bg.raised');
  const borderBase = tokens.getColor('border');
  const bgOverlay = tokens.getColor('bg.overlay');
  const selectedBg = tokens.getColor('bg.accent.solid');
  const selectedFg = tokens.getColor('fg.inverted');

  const calendarFont = tokens.getFont('S_Regular');
  const calendarHeaderFont = tokens.getFont('S_SemiBold');
  const headerFont = tokens.getFont('XS_Medium');

  return css`
    border-radius: ${tokens.getRadius('md')};
    border: ${borderWidth.sm} solid ${borderBase};
    background-color: ${bgOverlay};
    box-shadow: ${shadow.dropdown};
    z-index: ${zIndex.popover};
    padding: ${tokens.getSpacing('4')};
    min-width: ${CALENDAR_MIN_WIDTH};
    display: flex;
    flex-direction: column;
    gap: ${tokens.getSpacing('3')};
    outline: none;

    /* Navigation buttons */
    [data-part='prev-trigger'],
    [data-part='next-trigger'] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: ${tokens.getSpacing('8')};
      height: ${tokens.getSpacing('8')};
      padding: 0;
      background: transparent;
      border: ${borderWidth.sm} solid transparent;
      border-radius: ${tokens.getRadius('sm')};
      color: ${textColor};
      cursor: pointer;
      transition: background ${transition.fast};

      &:hover:not([data-disabled]) {
        background: ${bgHover};
      }

      &:focus-visible {
        background: ${bgHover};
        outline: ${focusRing.width} solid ${accentColor};
        outline-offset: -${focusRing.offset};
      }

      &[data-disabled] {
        opacity: ${DISABLED_OPACITY};
        cursor: not-allowed;
      }
    }

    /* View trigger (month/year label) */
    [data-part='view-trigger'] {
      display: inline-flex;
      flex: 1;
      align-items: center;
      justify-content: center;
      gap: ${tokens.getSpacing('1')};
      padding: ${tokens.getSpacing('1')} ${tokens.getSpacing('2')};
      background: transparent;
      border: none;
      border-radius: ${tokens.getRadius('xs')};
      color: ${textColor};
      font-family: ${calendarHeaderFont.fontFamily};
      font-size: ${calendarHeaderFont.size}px;
      font-weight: ${calendarHeaderFont.weight};
      cursor: pointer;
      transition: background ${transition.fast};

      &:hover {
        background: ${bgHover};
      }

      &:focus-visible {
        background: ${bgHover};
        outline: ${focusRing.width} solid ${accentColor};
        outline-offset: -${focusRing.offset};
      }
    }

    /* View control row */
    [data-part='view-control'] {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${tokens.getSpacing('2')};
    }

    /* Table */
    table[data-part='table'] {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
    }

    /* Table headers (weekday abbreviations) */
    th[data-part='table-header'] {
      padding: ${tokens.getSpacing('2')} 0;
      font-family: ${headerFont.fontFamily};
      font-size: ${headerFont.size}px;
      font-weight: ${headerFont.weight};
      color: ${fgMuted};
      text-align: center;
    }

    /* Table cells */
    td[data-part='table-cell'] {
      padding: 0;
      text-align: center;
    }

    /* All table cell triggers (day/month/year) */
    [data-part='table-cell-trigger'] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: ${tokens.getSpacing('9')};
      padding: 0;
      font-family: ${calendarFont.fontFamily};
      font-size: ${CELL_FONT_SIZE};
      font-weight: ${calendarFont.weight};
      background: transparent;
      border: none;
      border-radius: ${tokens.getRadius('sm')};
      color: ${textColor};
      cursor: pointer;
      user-select: none;
      outline: none;
      transition: background ${transition.fast};

      &:hover:not([data-disabled], [data-selected]) {
        background: ${bgHover};
      }

      &:is(:focus-visible, [data-focus]) {
        outline: ${focusRing.width} solid ${accentColor};
        outline-offset: -${focusRing.offset};
        position: relative;
        z-index: 1;
      }

      &[data-today] {
        font-weight: ${calendarHeaderFont.weight};
        color: ${accentColor};
      }

      &[data-selected] {
        background: ${selectedBg};
        color: ${selectedFg};
        font-weight: ${calendarHeaderFont.weight};
      }

      &[data-outside-range] {
        color: ${fgMuted};
        opacity: ${OUT_OF_RANGE_OPACITY};
      }

      &[data-disabled] {
        color: ${fgMuted};
        opacity: ${DISABLED_OPACITY};
        cursor: not-allowed;
      }

      &[data-unavailable] {
        color: ${fgMuted};
        text-decoration: line-through;
        opacity: ${DISABLED_OPACITY};
        cursor: not-allowed;
      }
    }

    /* Day cells: fixed square size */
    [data-part='table-cell-trigger'][data-view='day'] {
      width: ${tokens.getSpacing('10')};
    }

    /* Month/year cells: wider with horizontal padding */
    [data-part='table-cell-trigger'][data-view='month'],
    [data-part='table-cell-trigger'][data-view='year'] {
      height: ${tokens.getSpacing('10')};
      padding: 0 ${tokens.getSpacing('2')};
    }

    /* View containers */
    [data-part='view'] {
      display: flex;
      flex-direction: column;
      gap: ${tokens.getSpacing('3')};

      &[hidden] {
        display: none;
      }
    }
  `;
}

/* ─── Datetime time input ─── */

export function timeContainerStyle(tokens: DatepickerStyleTokens): SerializedStyles {
  const borderBase = tokens.getColor('border');
  const accentColor = tokens.getColor('border.accent');

  return css`
    display: flex;
    align-items: center;
    gap: ${tokens.getSpacing('2')};
    height: ${tokens.getSpacing('10')};
    padding: 0 ${tokens.getSpacing('3')};
    border: ${borderWidth.sm} solid ${borderBase};
    border-radius: ${tokens.getRadius('sm')};
    background: transparent;
    transition:
      border-color ${transition.fast},
      box-shadow ${transition.fast};

    &:focus-within {
      border-color: ${accentColor};
      box-shadow: 0 0 0 ${focusRing.width} ${accentColor};
    }
  `;
}

export function timeInputStyle(tokens: DatepickerStyleTokens): SerializedStyles {
  const calendarFont = tokens.getFont('S_Regular');
  const textColor = tokens.getColor('fg');

  return css`
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${calendarFont.fontFamily};
    font-size: ${calendarFont.size}px;
    color: ${textColor};

    /* Hide browser-native clock icon — we already show a CoreIcon on the left */
    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  `;
}

/* ─── Error block ─── */

export function errorStyle(tokens: DatepickerStyleTokens): SerializedStyles {
  const errorFont = tokens.getFont('S_Regular');

  return css`
    margin-top: ${tokens.getSpacing('2')};
    display: flex;
    align-items: center;
    gap: ${tokens.getSpacing('3')};
    color: ${tokens.getColor('fg.error')};
    font-family: ${errorFont.fontFamily};
    font-size: ${errorFont.size}px;
    font-weight: ${errorFont.weight};
    line-height: ${errorFont.lineHeight}px;
    letter-spacing: ${errorFont.letterSpacing};
  `;
}
